import React, { useMemo, useState } from 'react';

import { observer } from 'mobx-react';
import { Button, DropdownMenu, Select, Notification, DateRangePicker } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment-timezone';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { DropdownMenuItem } from '@bringg/react-components/dist/components/dropdown-menu/dropdown-menu';
import { TriggerType } from '@bringg/react-components/dist/components/popover/popover';
import { dateUtils } from '@bringg-frontend/utils';

import { useStores } from '../../../recipes';
import { NO_TEAM } from '../planned-delivery-windows-view';
import { CalendarViewMode } from 'bringg-web/features/planned-delivery-windows/calendar/planned-delivery-windows-calendar';
import { useDateFormat, useHasFeatureFlag } from 'bringg-web';
import { isDispatcher } from 'bringg-web/utils/privileges';
import { EXCLUSION_WINDOWS_CSV_IMPORT_FF } from '../constants';
import WeekDateRangePicker from './week-date-range/week-date-range-picker';

import styles from './planned-delivery-windows-toolbar.module.scss';

export type DropdownData = {
	items: DropdownMenuItem[];
	trigger: TriggerType[];
	text: string;
};

interface Props {
	selectedTeam: number;
	onTeamChange: (teamId: number) => void;
	changeCalendarRangeDates?: (startTime: string, endTime: string) => void;
	calendarRangeDates?: { startDate: string; endDate: string };
	timezone: string;
	dropdownData: DropdownData;
	isEditAllowed: boolean;
	calendarViewMode?: CalendarViewMode;
	setCalendarViewMode?: React.Dispatch<React.SetStateAction<CalendarViewMode>>;
	onExport?: () => Promise<void>;
	fetchDataByDate?: boolean;
}

const PlannedDeliveryWindowsToolbar = ({
	selectedTeam,
	onTeamChange,
	calendarRangeDates,
	changeCalendarRangeDates,
	timezone,
	dropdownData,
	isEditAllowed,
	calendarViewMode,
	setCalendarViewMode,
	onExport,
	fetchDataByDate
}: Props) => {
	const {
		teamsStore: { allCurrentUserTeams, all: allTeams },
		usersStore
	} = useStores();
	const { t } = useTranslation();
	const [exportIsLoading, setExportIsLoading] = useState(false);
	const useSlotsAndBlackoutsPrivilegesFeatureFlag = useHasFeatureFlag('use_slots_and_blackouts_privileges');
	const enableSlotsCompanyPrivilage = useHasFeatureFlag('Enable_slots_company_privilege');
	const hasExclusionWindowsCSVImportFF = useHasFeatureFlag(EXCLUSION_WINDOWS_CSV_IMPORT_FF);
	const fetchExclusionWindowsByTime = useHasFeatureFlag('fetch_exclusion_windows_by_time');
	const dateFormat = useDateFormat();
	const options = useMemo(() => {
		if (enableSlotsCompanyPrivilage && isDispatcher(usersStore.currentUser)) {
			return allCurrentUserTeams.map(({ id, name }) => ({ id, name }));
		}

		const byMerchantOption = [{ id: NO_TEAM, name: t('PLANNED_DELIVERY_WINDOWS.BY_MERCHANT') }];

		return [...byMerchantOption, ...allTeams.map(({ id, name }) => ({ id, name }))];
	}, [allCurrentUserTeams, allTeams, usersStore.currentUser, enableSlotsCompanyPrivilage]);
	const onTeamSelect = teamId => {
		onTeamChange(teamId);
	};

	const calendarViewModeOptions = [
		{ id: CalendarViewMode.week, name: t('PLANNED_DELIVERY_WINDOWS_TOOLBAR_CALENDAR_VIEW.WEEK_VIEW') },
		{ id: CalendarViewMode.day, name: t('PLANNED_DELIVERY_WINDOWS_TOOLBAR_CALENDAR_VIEW.DAY_VIEW') }
	];

	const onCalendarViewModeSelect = (calendarViewModeSelected: CalendarViewMode) => {
		const isWeekMode = calendarViewModeSelected === CalendarViewMode.week;
		let startRange = moment(calendarRangeDates.startDate).tz(timezone).startOf('week').toISOString();
		let endRange = moment(calendarRangeDates.startDate).tz(timezone).endOf(calendarViewModeSelected).toISOString();

		if (!isWeekMode) {
			const currentDayDate = dateUtils.getCurrentDayDateBetweenRangeDates(
				calendarRangeDates.startDate,
				calendarRangeDates.endDate,
				timezone
			);

			startRange = currentDayDate.clone().startOf('day').toISOString();
			endRange = currentDayDate.clone().endOf('day').toISOString();
		}

		changeCalendarRangeDates(startRange, endRange);
		setCalendarViewMode(calendarViewModeSelected);
	};

	const exportClick = async () => {
		setExportIsLoading(true);
		try {
			await onExport();
		} catch (error: unknown) {
			console.error(error);
			Notification.error(t('EXCLUSION_WINDOWS_MODAL_BUTTON_DROPDOWN.FAILED_TO_EXPORT'));
		} finally {
			setExportIsLoading(false);
		}
	};

	return (
		<div className={styles.toolbar}>
			<div className={styles.teamSelectionWrapper}>
				<Select
					className={styles.teamSelect}
					value={selectedTeam}
					options={options}
					showSearch
					onSelect={onTeamSelect}
					filterOption={(input, option) => option.title.toLowerCase().includes(input.toLowerCase())}
					placeholder={t('PLANNED_DELIVERY_WINDOWS_TOOLBAR.TEAM_SELECTION_INPUT')}
				/>

				<span className={styles.teamTimezone}>{`(${timezone || moment.tz.guess()})`}</span>
			</div>

			{fetchDataByDate ? (
				fetchExclusionWindowsByTime ? (
					<DateRangePicker
						data-test-id="planned-delivery-windows-toolbar-date-range-picker"
						format={dateFormat}
						startDate={calendarRangeDates?.startDate}
						endDate={calendarRangeDates?.endDate}
						translations={{
							fromDate: t('SERVICE_AREA.START_DATE'),
							toDate: t('SERVICE_AREA.END_DATE')
						}}
						onChange={(dates: Moment[]) => {
							if (dates?.[0] && dates[1]) {
								changeCalendarRangeDates(dates[0].toISOString(), dates[1].toISOString());
							}
						}}
					/>
				) : (
					<div />
				)
			) : (
				<WeekDateRangePicker
					calendarRangeDates={calendarRangeDates}
					changeCalendarRangeDates={changeCalendarRangeDates}
					timezone={timezone}
					calendarViewMode={calendarViewMode}
				/>
			)}
			<div className={styles.actions}>
				{calendarViewMode && (
					<Select
						className={styles.calendarViewMode}
						value={calendarViewMode}
						options={calendarViewModeOptions}
						onSelect={onCalendarViewModeSelect}
						placeholder={t('PLANNED_DELIVERY_WINDOWS_TOOLBAR_CALENDAR_VIEW.CALENDAR_VIEW_MODE_PLACEHOLDER')}
					/>
				)}
				{!calendarViewMode && hasExclusionWindowsCSVImportFF && onExport ? (
					<Button loading={exportIsLoading} type="ghost" onClick={exportClick}>
						{t('PLANNED_DELIVERY_WINDOWS_TOOLBAR.EXPORT_CSV')}
					</Button>
				) : null}
				<div className={styles.addButton}>
					<DropdownMenu items={dropdownData.items} trigger={dropdownData.trigger}>
						<Button
							type="primary"
							disabled={useSlotsAndBlackoutsPrivilegesFeatureFlag ? !isEditAllowed : false}
							data-test-id="add-button"
						>
							{dropdownData.text} <BringgIcon iconName={BringgFontIcons.Chevron} />
						</Button>
					</DropdownMenu>
				</div>
			</div>
		</div>
	);
};

export default observer(PlannedDeliveryWindowsToolbar);
